import React, { useEffect } from 'react';
import { AppBar, Popover, InputBase, Box, Typography, Tooltip, Stack, IconButton } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { SettingsOutlined, Search, Close, PersonOutlineOutlined, AppsOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import { i18next } from '@/config/i18n';
import Dropdown from '../App/Dropdown';
import { makeStyles } from '@mui/styles';
import logoMarkWhite from '../Common/Logo/logo_mark_white.svg';
import { isEqual } from 'underscore';
import { PlanningActions } from '../Plannings/PlanningActions/PlanningActions';
import { withRouter } from '@/hooks';
import SettingEditor from '../App/SettingEditor';
import { useEditMode } from '@/contexts/EditModeContext';
import { flushSync } from 'react-dom';
import { useActiveSite } from '@/hooks/useActiveSite';
import { styled } from '@mui/material/styles';

interface RootState {
  core: {
    process: any;
    ui: string[];
    selected_process?: { optimization_id?: string | number };
  };
  router: {
    location: { pathname: string };
  };
}

const useStyles = makeStyles(() => ({
  root: { flexGrow: 1 },
  logo: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    objectFit: 'contain',
  },
  appBar: {
    position: 'static',
    boxShadow: 'none',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 64,
    gap: 4,
  },
  leftCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 'calc(100% - 360px)',
    overflow: 'visible',
  },
  kpiCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    minWidth: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingRight: 12,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  rightCluster: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flex: '0 0 360px',
  },
  editButton: {
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '.6px',
    padding: '11px',
    background: 'rgba(255,255,255,0.25)',
    color: '#ffffff',
    boxShadow: 'none',
    borderRadius: '3px',
    width: '194px'
  },
  iconButton: {
    padding: '9px',
  },
  headerIcon: {
    fontSize: 24,
  },
  searchInput: {
    width: '100%',
    padding: '5px',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '16px',
  },
  '@media (max-width: 1400px)': {
    leftCluster: {
      maxWidth: 'calc(100% - 320px)',
    },
    rightCluster: {
      flex: '0 1 320px',
    },
    editButton: {
      fontSize: '11px',
      width: '179px'
    },
    kpiCluster: {
      gap: 8
    }
  },
  '@media (max-width: 1357px)': {
    leftCluster: {
      maxWidth: 'calc(100% - 320px)',
    },
    rightCluster: {
      flex: '0 1 320px',
    },
    editButton: {
      fontSize: '11px',
      width: '179px'
    },
    kpiCluster: {
      gap: 3
    },
    iconButton: {
      padding: 0,
    },
    headerIcon: {
      fontSize: 21,
    },
  },
}));

const CenteredDarkTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#2F343A',
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    padding: '8px 10px',
    borderRadius: 8,
    boxShadow: '0 6px 16px rgba(0,0,0,.25)',
  },
  '& .MuiTooltip-arrow': { color: '#2F343A' },
  '&[data-popper-placement*="top"] .MuiTooltip-arrow, &[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
    left: '50% !important',
    transform: 'translateX(-50%) !important',
  },
  '&[data-popper-placement*="left"] .MuiTooltip-arrow, &[data-popper-placement*="right"] .MuiTooltip-arrow': {
    top: '50% !important',
    transform: 'translateY(-50%) !important',
  },
}));

interface HeaderProps { router: { location: { pathname: string } } }

const Header: React.FC<HeaderProps> = (props) => {
  const [showSearch, setShowSearch] = React.useState(false);
  const [searchVal, setSearchVal] = React.useState('');
  const [showProcess, setProcess] = React.useState(false);
  const [processAnchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const [editorAnchor, setEditorAnchor] = React.useState<HTMLElement | null>(null);
  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null);
  const [action, setAction] = React.useState<string | null>(null);
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();

  const { process, ui: frontendFlags, selected_process } = useSelector((state: RootState) => state.core, isEqual);
  const router = useSelector((state: RootState) => state.router, isEqual);

  const { editMode, setEditMode } = useEditMode();

  useEffect(() => {
    const paths = ['jobs', 'routes', 'plannings', 'dashboard', 'summary'];
    const found = paths.find((x) => props.router.location.pathname.includes(x));
    setAction(found || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  function closeProcess() {
    setProcess(false);
    setAnchor(null);
  }
  const toggleProfilePopover = (e: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(profileAnchor ? null : e.currentTarget);
  };
  const toggleSearch = () => setShowSearch(!showSearch);
  const toggleEditorPopover = (e?: React.MouseEvent<HTMLElement>) => {
    setEditorAnchor(e ? e.currentTarget : null);
  };

  const onEditButtonClick = () => {
    flushSync(() => setEditMode(true));
    navigate(`/routes/routelistmap/${selected_process?.optimization_id}`, { state: { editMode: true } });
  };

  const handleDismiss = () => {
    window.dispatchEvent(new CustomEvent('routes:header-discard'));
    setEditMode(false);
  };
  const handleSave = () => {
    window.dispatchEvent(new CustomEvent('routes:header-save'));
  };

  return (
    <div className={classes.root}>
      <AppBar color="primary" className={classes.appBar}>
        {/* ONE ROW LAYOUT: LEFT (logo+KPIs) | RIGHT (edit+icons) */}
        <Box className={classes.toolbarRow}>
          {/* LEFT side: logo + KPIs */}
          <Box className={classes.leftCluster}>
            <Link to="/dashboard" className={classes.logo}>
              <img alt="Smartlane logo" src={logoMarkWhite} height={40} />
            </Link>

            <Box className={classes.kpiCluster}>
              <PlanningActions />
            </Box>

            <Popover
              open={showProcess}
              anchorEl={processAnchor}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              PaperProps={{ style: { width: 400 } }}
              style={{ zIndex: 1000 }}
              onClose={closeProcess}
            />
          </Box>

          {/* RIGHT side: ALWAYS STICK RIGHT */}
          <Box className={classes.rightCluster}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {/* <IconButton aria-label="search" onClick={toggleSearch} size="large" sx={{ p: '9px' }}>
                <Search color="bright" />
              </IconButton> */}


              {!frontendFlags.includes('no_settings') && (
                <IconButton aria-label="shipment settings" onClick={toggleEditorPopover} size="large" sx={{ p: '9px' }}>
                  <SettingsOutlined color="bright" />
                </IconButton>
              )}

              <IconButton aria-label="open profile menu" onClick={toggleProfilePopover} size="large" sx={{ p: '9px' }}>
                <PersonOutlineOutlined color="bright" />
              </IconButton>
            </Stack>

            {activeSite && (
              <CenteredDarkTooltip title={`${activeSite}`} placement="bottom">
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ cursor: 'pointer', color: '#fff', fontWeight: 600, fontSize: '13px', paddingleft: '7px', paddingRight: '7px', opacity: 0.9, maxWidth: 180, textOverflow: 'ellipsis' }}
                >
                  {activeSite}
                </Typography>
              </CenteredDarkTooltip>
            )}
          </Box>
        </Box>
      </AppBar>

      {/* SEARCH: fixed width + anchored near right */}
      {showSearch && (
        <Box
          sx={{
            position: 'absolute',
            top: 67,
            left: '80%',
            transform: 'translateX(-50%)',
            width: '520px',
            maxWidth: '520px',
            zIndex: 1200,
            bgcolor: 'white',
            p: 0.75,
            borderRadius: 1,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #eee',
          }}
        >
          <InputBase
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder={i18next.t('common:routes.search')}
            className={classes.searchInput}
            startAdornment={<Search />}
            endAdornment={
              searchVal ? (
                <IconButton onClick={() => setSearchVal('')} id="clear-search" size="large">
                  <Close color="inherit" />
                </IconButton>
              ) : null
            }
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      <Dropdown open={profileAnchor} toggle={toggleProfilePopover} />
      <SettingEditor anchorEl={editorAnchor} toggle={() => toggleEditorPopover()} />
    </div>
  );
};

export default withRouter(Header);
